import { BlockDataProvider, PageLayout } from "@diality/ui";
import type { EntrySummary } from "@diality/contracts";
import type { DataRequest } from "@diality/ui";
import { apiUrl, resolveApiEndpoint } from "./api-endpoint";
import type { MarketingPageLoaderData } from "./page-loader";
import { articleStructuredData } from "./seo";

async function loadEntries(
  request: DataRequest,
  offset: number,
  signal: AbortSignal,
) {
  const endpoint = await resolveApiEndpoint({ signal });
  const params = new URLSearchParams({ offset: String(offset) });
  if (request.limit) params.set("limit", String(request.limit));
  if (request.sort) params.set("sort", request.sort);
  if (request.dir) params.set("dir", request.dir);
  for (const filter of request.filters ?? []) {
    params.append("filter", `${filter.field}:${filter.op}:${filter.value}`);
  }
  const response = await fetch(
    apiUrl(
      endpoint,
      `/api/collections/${encodeURIComponent(request.collectionId)}/entries?${params}`,
    ),
    { signal },
  );
  if (!response.ok) throw new Error("Unable to load more news");
  return response.json() as Promise<{
    entries: EntrySummary[];
    nextOffset: number | null;
  }>;
}

/**
 * A CMS-composed page, drawn in its template's layout.
 *
 * The one place the public site turns a loader result into a page, so
 * MarketingPage and the catch-all 404 route cannot disagree about how a
 * template is honoured.
 *
 * `data-page-layout` lives HERE, on the route's own wrapper, and deliberately
 * not inside PageLayout. PageLayout is shared with the CMS canvas and preview;
 * a hook on it would be carried by all three, and a visitor assertion could
 * then pass against a page no visitor can reach. Same reasoning as the
 * renderer hooks in docs/briefs/compose-featured-press.md §2.
 */
export function CmsPageView({
  data,
  children,
}: {
  data: Extract<MarketingPageLoaderData, { kind: "cms" }>;
  children: React.ReactNode;
}) {
  const headerBlock = data.blocks.find(
    (block) => block.componentId === "ArticleHeader",
  );
  const article =
    data.entry ??
    (headerBlock
      ? {
          heading: headerBlock.scalars?.heading || data.page.title,
          date: headerBlock.scalars?.date || data.publishedAt || undefined,
        }
      : null);

  return (
    <div data-page-layout={data.template.layout}>
      {article ? (
        <script type="application/ld+json">
          {JSON.stringify(
            articleStructuredData({
              headline: article.heading,
              datePublished: article.date ?? data.publishedAt,
            }),
          )}
        </script>
      ) : null}
      {/* The entries the page's blocks list but do not own, fetched by the
          loader before a byte of HTML was sent. Provided here rather than
          inside PageLayout because it belongs to the PAGE's data, not to the
          shape it is drawn in. */}
      <BlockDataProvider value={data.blockData} loadEntries={loadEntries}>
        <PageLayout
          layout={data.template.layout}
          // Both are null for an ordinary page, and the article layout renders
          // its column without a masthead — which is what let the layout ship
          // before entries existed at all.
          entry={data.entry ?? undefined}
          backHref={data.entryBackHref ?? undefined}
        >
          {children}
        </PageLayout>
      </BlockDataProvider>
    </div>
  );
}
